import {MouseEvent, useCallback, useEffect, useRef} from 'react';
import {requestContext} from '../../../../../../common/requestContext';
import {usePrevious} from '../../../../../../common/shared/hooks/usePrevious';
import {useWindowInnerHeight} from '../../../../../../common/shared/hooks/useWindowInnerHeight';
import {useWindowInnerWidth} from '../../../../../../common/shared/hooks/useWindowInnerWidth';
import {twMerge} from '../../../../../../common/twMerge';
import useTypedDispatch from '../../../../../../common/useTypedDispatch';
import useTypedSelector from '../../../../../../common/useTypedSelector';
import {Icon} from '../../../../../../design-system/atoms/Icon/Icon';
import {IconType} from '../../../../../../design-system/atoms/Icon/IconType.enum';
import {WebpImageWithApngFallback} from '../../../../../../design-system/organisms/WebpImageWithApngFallback/WebpImageWithApngFallback';
import {WebpImageElement} from '../../../../../../design-system/organisms/WebpImageWithApngFallback/WepImageElement.interface';
import {events} from '../constant/talkViewerEvents';
import {TalkDataType} from '../type/TalkDataType.enum';
import {ZIndex} from '../type/ZIndex.enum';
import {getDataUrlOrUrl} from '../util/getDataUrlOrUrl';

export function TalkViewerFullscreenContainer() {
  const fullscreenTalk = useTypedSelector(state => {
    return state.talkViewer.fullscreen
      ? state.talkViewer.talkMap[state.talkViewer.fullscreen.talkId]
      : undefined;
  });
  const preloadedMedia = useTypedSelector(
    state => state.talkViewer.preloadedMedia
  );
  const isShowFullscreenImage =
    fullscreenTalk?.datatype === TalkDataType.FullscreenImage;
  const dispatch = useTypedDispatch();
  // 풀스크린 이미지 자체를 눌렀을 때
  const onClickFullscreenImage = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      dispatch(events.fullscreenImageTapPressed());
    },
    [dispatch]
  );

  // 풀스크린 이미지 닫기버튼을 눌렀을 때
  const onClickCloseFullscreenImage = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      dispatch(events.fullscreenHideRequested());
    },
    [dispatch]
  );
  const currentFullscreenSource = fullscreenTalk?.media?.source;
  const prevFullscreenSource = usePrevious(currentFullscreenSource);
  // 풀스크린이 내려갈 때 잔상을 남기기 위해 현재 풀스크린이 오픈상태가 아니면 이전 풀스크린 소스를 남겨둔다.
  const fullscreenSource = isShowFullscreenImage
    ? currentFullscreenSource
    : prevFullscreenSource;
  const fullscreenImageUrl = fullscreenSource?.url;
  const isWebp = fullscreenImageUrl?.includes('.webp');
  const apngUrl = fullscreenImageUrl?.replace('.webp', '.png');
  const imageRef = useRef<WebpImageElement>(null);

  const imageWidth = fullscreenSource?.width || 0;
  const imageHeight = fullscreenSource?.height || 0;
  const windowInnerWidth = useWindowInnerWidth();
  const windowInnerHeight = useWindowInnerHeight();

  // 풀스크린 상태일 때 가로로 100% 늘려줘야 하는 지 여부. false면 세로로 늘려준다.
  const isNeedHorizontalStretch =
    imageWidth / imageHeight > windowInnerWidth / windowInnerHeight;

  useEffect(() => {
    if (isShowFullscreenImage) {
      imageRef.current?.replay?.();
    }
  }, [fullscreenImageUrl, isShowFullscreenImage]);

  return (
    <div
      className={twMerge(
        'fixed top-0 left-0 box-border flex h-full w-full items-center justify-center overflow-hidden bg-static-mono-black transition-opacity duration-1000',
        !isShowFullscreenImage && 'pointer-events-none opacity-0',
        'pr-safe pl-safe'
      )}
      style={{
        zIndex: ZIndex.FullScreen,
      }}
      onClick={onClickFullscreenImage}
    >
      <WebpImageWithApngFallback
        imageUrl={
          fullscreenImageUrl
            ? getDataUrlOrUrl(fullscreenImageUrl, preloadedMedia)
            : undefined
        }
        apngUrl={apngUrl ? getDataUrlOrUrl(apngUrl, preloadedMedia) : undefined}
        // object-fit: contain과 같은 효과를 주기 위해 구현한 스타일. object-fit: contain을 쓴 경우 ios12버전에서 이미지가 뜨기 전 깜빡거리는 현상이 있어서 대체하였다.
        width={isNeedHorizontalStretch ? '100%' : 'auto'}
        height={isNeedHorizontalStretch ? 'auto' : '100%'}
        isIos={requestContext.requestInfo.display.isIos}
        isWebp={isWebp}
        ref={imageRef}
      />
      <Icon
        type={IconType.BtnFullscreenVideoClose}
        onClick={onClickCloseFullscreenImage}
        className="absolute top-10tvs right-10tvs box-content h-50tvs w-50tvs p-20tvs mr-safe"
        style={{
          zIndex: ZIndex.FullScreen + 1,
        }}
      />
    </div>
  );
}
